import React from 'react';
import { Box, Typography, Breadcrumbs, Link as MuiLink, useTheme, alpha } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLayout } from '../../contexts/LayoutContext';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: { label: string; href?: string }[];
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ 
  title, 
  subtitle, 
  breadcrumbs,
  actions 
}) => {
  const theme = useTheme();
  const { preferences } = useLayout();
  const isSidebarLayout = preferences.layoutMode === 'sidebar';
  
  return (
    <Box sx={{ 
      mb: 4,
      position: 'relative',
      // Only make draggable in sidebar mode (top nav has its own drag region)
      ...(isSidebarLayout && {
        '@media (display-mode: window-controls-overlay)': {
          WebkitAppRegion: 'drag',
          appRegion: 'drag',
        }
      })
    }}>
      {breadcrumbs && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" />}
            sx={{ 
              mb: 2,
              // Make breadcrumb links clickable in sidebar mode
              ...(isSidebarLayout && {
                '@media (display-mode: window-controls-overlay)': {
                  WebkitAppRegion: 'no-drag',
                  appRegion: 'no-drag',
                }
              })
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              
              if (crumb.href && !isLast) {
                return (
                  <MuiLink
                    key={index}
                    component={RouterLink}
                    to={crumb.href}
                    underline="hover"
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {crumb.label}
                  </MuiLink>
                );
              }
              
              return (
                <Typography
                  key={index}
                  color="text.primary"
                  sx={{ 
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}
                >
                  {crumb.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        </motion.div>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{
          // Make text selectable in sidebar mode
          ...(isSidebarLayout && {
            '@media (display-mode: window-controls-overlay)': {
              WebkitAppRegion: 'no-drag',
              appRegion: 'no-drag',
            }
          })
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ 
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: subtitle ? 1 : 0,
                userSelect: 'text',  // Ensure text is selectable
              }}
            >
              {title}
            </Typography>
          </motion.div>
          
          {subtitle && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ maxWidth: 600 }}
              >
                {subtitle}
              </Typography>
            </motion.div>
          )}
        </Box>
        
        {actions && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
            }}>
              {actions}
            </Box>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};